"use client";

import * as React from "react";
import { Box, Card, Flex, Text } from "@radix-ui/themes";
import { Button, Skeleton } from "./elements.js";
import { IconPanel } from "./icon-panel.js";
import {
  ExternalLinkIcon,
  InfoCircledIcon,
  Cross2Icon,
} from "@radix-ui/react-icons";
import * as CardList from "./card-list.js";
import { ProviderIcon } from "./provider-icon.js";
import {
  getIdentityProviderName,
  type IdentityProvider,
} from "./identity-providers.js";
import { Status } from "./status.js";
import {
  getDomProps,
  unreachable,
  type WidgetRootState,
  type WidgetRootDomProps,
} from "./utils.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";
import { getErrorMessage } from "./generic-error.js";

interface NotConfiguredProps {
  connectionStatus: "NotConfigured";
  identityProvider?: never;
  expiryDate?: never;
}

interface InactiveProps {
  connectionStatus: "Inactive";
  identityProvider: IdentityProvider;
  expiryDate?: never;
}

interface ActiveProps {
  connectionStatus: "Active";
  identityProvider: IdentityProvider;
  expiryDate?: never;
}

interface RequiresActionProps {
  connectionStatus: "Expired" | "Expiring";
  identityProvider: IdentityProvider;
  expiryDate: Date | null;
}

export type AdminPortalSsoConnectionStatusProps =
  | NotConfiguredProps
  | InactiveProps
  | ActiveProps
  | RequiresActionProps;

type AdminPortalSsoConnectionProps = WidgetRootDomProps &
  AdminPortalSsoConnectionStatusProps & {
    currentDate: Date | null;
    lastSession: Date | null;
    adminPortalOpenButton: React.ReactNode;
  };

interface AdminPortalSsoConnectionContextValue {
  connectionStatus: AdminPortalSsoConnectionStatusProps["connectionStatus"];
}

const AdminPortalSsoConnectionContext =
  React.createContext<AdminPortalSsoConnectionContextValue | null>(null);
AdminPortalSsoConnectionContext.displayName = "AdminPortalSsoConnectionContext";

function useAdminPortalSsoConnectionContext() {
  const context = React.useContext(AdminPortalSsoConnectionContext);
  if (!context) {
    throw new Error(
      "useAdminPortalSsoConnectionContext must be used within a AdminPortalSsoConnectionContext provider",
    );
  }
  return context;
}

const AdminPortalSsoConnection: React.FC<AdminPortalSsoConnectionProps> = ({
  currentDate,
  connectionStatus,
  identityProvider,
  expiryDate,
  lastSession,
  adminPortalOpenButton,
  ...domProps
}) => {
  return (
    <CardList.Root size="2" {...getWidgetRootDomProps("resolved", domProps)}>
      <AdminPortalSsoConnectionContext.Provider value={{ connectionStatus }}>
        <CardList.Item>
          <Flex direction="row" justify="between" align="center" gap="2">
            {(() => {
              if (connectionStatus === "NotConfigured") {
                return (
                  <>
                    <Text size="2" color="gray">
                      <Translation
                        defaultMessage="You haven't set up Single Sign-On yet."
                        id="KDs1Ib"
                        description="Empty state message when SSO is not configured"
                      />
                    </Text>
                    {adminPortalOpenButton}
                  </>
                );
              }

              return (
                <>
                  <SessionInfo
                    connectionStatus={connectionStatus}
                    identityProvider={identityProvider}
                    lastSession={lastSession}
                    currentDate={currentDate}
                  />
                  <Flex gap="5" align="center">
                    <SsoStatus status={connectionStatus} />
                    {adminPortalOpenButton}
                  </Flex>
                </>
              );
            })()}
          </Flex>
        </CardList.Item>
        {connectionStatus === "Expired" && (
          <CardList.Item>
            <Flex align="start" gap="2">
              <Box asChild mt="2px" flexShrink="0">
                <InfoCircledIcon color="gray" />
              </Box>
              {(() => {
                if (connectionStatus === "Expired") {
                  return (
                    <Text size="2" color="gray">
                      {(() => {
                        if (!expiryDate) {
                          return (
                            <Translation
                              defaultMessage="The SAML response signing certificate has expired. Users won't be able to sign-in to the application until the certificate is renewed."
                              id="5tX19v"
                              description="Error message when SAML certificate has expired (no date)"
                            />
                          );
                        }

                        return (
                          <Translation
                            defaultMessage="The SAML response signing certificate expired on {expiryDate}. Users won't be able to sign-in to the application until the certificate is renewed."
                            id="vmTW/h"
                            description="Error message when SAML certificate has expired with date"
                            values={{
                              expiryDate: (
                                <Text weight="bold" as="span">
                                  {expiryDate?.toLocaleString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </Text>
                              ),
                            }}
                          />
                        );
                      })()}
                    </Text>
                  );
                }

                if (connectionStatus === "Expiring") {
                  return (
                    <Text size="2" color="gray">
                      {(() => {
                        if (!expiryDate) {
                          return (
                            <Translation
                              defaultMessage="The SAML response signing certificate is expiring soon. When expired, users won't be able to sign-in."
                              id="3dgA5f"
                              description="Warning message when SAML certificate is expiring soon (no date)"
                            />
                          );
                        }

                        return (
                          <Translation
                            defaultMessage="The SAML response signing certificate will expire on {expiryDate}. When expired, users won't be able to sign-in."
                            id="xT9Exy"
                            description="Warning message when SAML certificate is expiring with date"
                            values={{
                              expiryDate: (
                                <Text weight="bold" as="span">
                                  {expiryDate?.toLocaleString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </Text>
                              ),
                            }}
                          />
                        );
                      })()}
                    </Text>
                  );
                }
              })()}
            </Flex>
          </CardList.Item>
        )}
      </AdminPortalSsoConnectionContext.Provider>
    </CardList.Root>
  );
};

function SsoStatus({
  status,
}: {
  status: AdminPortalSsoConnectionStatusProps["connectionStatus"];
}) {
  if (status === "NotConfigured") {
    return null;
  }

  if (status === "Inactive") {
    return (
      <Status state="waiting">
        <Translation
          defaultMessage="Setup in progress"
          id="MQR5mR"
          description="Status label when SSO setup is incomplete"
        />
      </Status>
    );
  }

  if (status === "Expired") {
    return (
      <Status state="error">
        <Translation
          defaultMessage="Requires action"
          id="Yo2vHC"
          description="Status label when SSO requires attention"
        />
      </Status>
    );
  }

  if (status === "Active" || status === "Expiring") {
    return (
      <Status state="success">
        <Translation
          defaultMessage="Connected"
          id="8S+Cyw"
          description="Status label when SSO is active"
        />
      </Status>
    );
  }

  return unreachable(status);
}

function SessionInfo({
  currentDate,
  identityProvider,
  lastSession,
  connectionStatus,
}: {
  identityProvider: IdentityProvider;
  lastSession: Date | null;
  currentDate: Date | null;
  connectionStatus: AdminPortalSsoConnectionStatusProps["connectionStatus"];
}) {
  const relativeTimeString = React.useMemo(() => {
    if (
      !lastSession ||
      !currentDate ||
      connectionStatus === "NotConfigured" ||
      connectionStatus === "Inactive"
    ) {
      return null;
    }

    return getRelativeTimeString(currentDate, lastSession);
  }, [lastSession, currentDate, connectionStatus]);

  return (
    <Flex gap="4" align="center">
      <IconPanel color="panel">
        <ProviderIcon provider={identityProvider} size="2" />
      </IconPanel>
      {lastSession ? (
        <Flex direction="column">
          <Text size="2" weight="bold">
            {getIdentityProviderName(identityProvider)}
          </Text>
          {relativeTimeString ? (
            <Text color="gray" size="2">
              <Translation
                defaultMessage="Last session {relativeTime}"
                id="yN72Gc"
                description="Label showing when the last SSO session occurred"
                values={{ relativeTime: relativeTimeString }}
              />
            </Text>
          ) : null}
        </Flex>
      ) : (
        <Text size="2" weight="bold">
          {getIdentityProviderName(identityProvider)}
        </Text>
      )}
    </Flex>
  );
}

function AdminPortalOpenButton({
  isPending,
  initConfig,
}: {
  isPending: boolean;
  initConfig: () => void;
}) {
  const { connectionStatus } = useAdminPortalSsoConnectionContext();
  const label = (() => {
    switch (connectionStatus) {
      case "NotConfigured":
        return (
          <Translation
            defaultMessage="Set up SSO"
            id="MtkNQO"
            description="Button label to start SSO setup"
          />
        );
      case "Inactive":
        return (
          <Translation
            defaultMessage="Continue setup"
            id="HFxrbY"
            description="Button label to continue incomplete SSO setup"
          />
        );
      case "Active":
      case "Expiring":
      case "Expired":
        return (
          <Translation
            defaultMessage="Manage"
            id="T1VKzP"
            description="Button label to manage SSO settings"
          />
        );
      default:
        return unreachable(connectionStatus);
    }
  })();

  return (
    <Button
      variant="secondary"
      loading={isPending}
      disabled={isPending}
      onClick={initConfig}
    >
      {label} <ExternalLinkIcon aria-hidden />
    </Button>
  );
}

interface AdminPortalSsoConnectionLoadingProps extends WidgetRootDomProps {}

const AdminPortalSsoConnectionLoading: React.FC<
  AdminPortalSsoConnectionLoadingProps
> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <Flex direction="row" justify="between" align="center" gap="2">
        <Flex gap="4" align="center">
          <Skeleton>
            <IconPanel color="panel">
              <ProviderIcon provider="okta" size="2" />
            </IconPanel>
          </Skeleton>
          <Flex direction="column" gap="1" my="-4px">
            <Skeleton>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <Text size="1">Okta</Text>
            </Skeleton>
            <Skeleton>
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              <Text size="1">Last session 10 minutes ago</Text>
            </Skeleton>
          </Flex>
        </Flex>
        <Flex gap="5" align="center">
          <Skeleton>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Status state="error">Requires action</Status>
          </Skeleton>
          <Skeleton>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Button variant="secondary">
              Manage <ExternalLinkIcon aria-hidden />
            </Button>
          </Skeleton>
        </Flex>
      </Flex>
    </Card>
  );
};

interface AdminPortalSsoConnectionErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const AdminPortalSsoConnectionError: React.FC<
  AdminPortalSsoConnectionErrorProps
> = ({ error, ...domProps }) => {
  const translate = useTranslation();
  React.useEffect(() => {
    console.error(error);
  }, [error]);

  const { heading, message } = getErrorMessage(error, translate);

  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <Flex direction="row" justify="between" align="center" gap="2">
        <Flex gap="4" align="center">
          <Flex
            align="center"
            justify="center"
            width="24px"
            height="24px"
            style={{
              borderRadius: "9999px",
              backgroundColor: "var(--red-a4)",
              color: "var(--red-a11)",
              flexShrink: 0,
            }}
          >
            <Cross2Icon width="18px" height="18px" />
          </Flex>
          <Flex direction="column">
            <Text size="2" weight="bold">
              {heading}
            </Text>
            <Text size="2" color="gray">
              {message}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

function getRelativeTimeString(
  currentDate: Date | null,
  lastSession: Date | null,
) {
  if (!currentDate || !lastSession) {
    return null;
  }

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diff = lastSession.getTime() - currentDate.getTime();

  const diffSeconds = Math.round(diff / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffMonths = Math.round(diffDays / 30);
  const diffYears = Math.round(diffMonths / 12);

  if (Math.abs(diffSeconds) < 60) {
    return "now";
  }

  if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, "minute");
  }

  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, "hour");
  }

  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, "day");
  }

  if (Math.abs(diffMonths) < 12) {
    return rtf.format(diffMonths, "month");
  }

  return rtf.format(diffYears, "year");
}

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "admin-portal-sso-connection",
    widgetState: state,
  });
}

export type {
  AdminPortalSsoConnectionProps,
  AdminPortalSsoConnectionLoadingProps,
  AdminPortalSsoConnectionErrorProps,
};
export {
  AdminPortalSsoConnection,
  AdminPortalSsoConnectionLoading,
  AdminPortalSsoConnectionError,
  /** @internal */
  AdminPortalOpenButton,
};
